import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  getReviewLinksStore,
  storeCreateReviewLink,
  storeUpdateReviewLink,
  storeDeleteReviewLink,
} from '../../../../../store/ratings';
import { useResponseHandler } from '../../../../../hooks/useResponseHandler';
import { ModalConfirmDeactivation } from '../../../../../components/modals/ModalConfirmDeactivation';
import { hasPemissions } from '../../../../../commons/utils';

const ICONS = [
  { name: 'google.png', platform: 'google', label: 'Google' },
  { name: 'facebook.png', platform: 'facebook', label: 'Facebook' },
  { name: 'instagram.png', platform: 'instagram', label: 'Instagram' },
];

export const ReviewLinksCard = () => {
  const dispatch = useDispatch<any>();
  const { review_links_list } = useSelector((state: any) => state.ratingsDevices);
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const [selectedIcon, setSelectedIcon] = useState('');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openModalDeactivation, setOpenModalDeactivation] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null);

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.link.resenas',
      'view'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.link.resenas',
      'edit'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.link.resenas',
      'delete'
    );
    if (del) setCanDelete(true);

    const create = await hasPemissions(
      'modulo.calificaciones',
      'modulo.calificacion.link.resenas',
      'create'
    );
    if (create) setCanCreate(true);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      const fetchData = async () => {
        const response = await dispatch(getReviewLinksStore());
        await handleResponse(response, 'obtener links para compartir reseñas', false);
      };
      fetchData();
    }
  }, [canView]);

  const availableIcons = ICONS;

  const handleSave = async () => {
    if (!selectedIcon || !url.trim() || !name.trim()) return;

    setLoading(true);
    const selectedIconData = ICONS.find((i) => i.name === selectedIcon);
    if (!selectedIconData) {
      setLoading(false);
      return;
    }

    const payload = {
      name: name.trim(),
      icon: selectedIconData.name,
      platform: selectedIconData.platform,
      url: url.trim(),
    };

    let response;
    if (editingId) {
      response = await dispatch(storeUpdateReviewLink(editingId, payload));
      await handleResponse(response, 'actualizar link', true);
    } else {
      response = await dispatch(storeCreateReviewLink(payload));
      await handleResponse(response, 'crear link', true);
    }

    if (response?.ok) {
      handleCancelEdit();
    }
    setLoading(false);
  };

  const handleEdit = (link: any) => {
    setEditingId(link.id);
    setSelectedIcon(link.icon);
    setName(link.name || '');
    setUrl(link.url);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setSelectedIcon('');
    setName('');
    setUrl('');
  };

  const handleDelete = (id: string) => {
    setLinkToDelete(id);
    setOpenModalDeactivation(true);
  };

  const confirmDeactivate = async () => {
    if (!linkToDelete) return;
    const response = await dispatch(storeDeleteReviewLink(linkToDelete));
    await handleResponse(response, 'eliminar link', true);
    if (editingId === linkToDelete) {
      handleCancelEdit();
    }
    setOpenModalDeactivation(false);
    setLinkToDelete(null);
  };

  const handleVerify = (url: string) => {
    window.open(url, '_blank');
  };

  if (!hasPermission) return <>{ResponseComponents}</>;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
      }}
    >
      {ResponseComponents}
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Gestión de Links de Reseñas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configura los enlaces directos para que los clientes puedan dejarte una reseña en las
          redes sociales.
        </Typography>

        {(canCreate || canEdit) && (
          <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="icon-select-label">Red Social</InputLabel>
              <Select
                labelId="icon-select-label"
                value={selectedIcon}
                label="Red Social"
                onChange={(e) => setSelectedIcon(e.target.value)}
                disabled={!canCreate && !editingId}
              >
                {availableIcons.length === 0 && (
                  <MenuItem disabled value="">
                    Todas asignadas
                  </MenuItem>
                )}
                {availableIcons.map((icon) => (
                  <MenuItem key={icon.name} value={icon.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={`${window.location.origin}/social/${icon.name}`}
                        alt={icon.label}
                        sx={{ width: 24, height: 24 }}
                        variant="square"
                      />
                      {icon.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              sx={{ flexGrow: 1, minWidth: 150 }}
              size="small"
              label="Nombre *"
              value={name}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Mi Facebook"
              disabled={!canCreate && !editingId}
            />

            <TextField
              sx={{ flexGrow: 1, minWidth: 200 }}
              size="small"
              label="URL del enlace *"
              value={url}
              autoComplete="off"
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              disabled={!canCreate && !editingId}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!selectedIcon || !url.trim() || !name.trim() || loading}
                startIcon={editingId ? <EditIcon /> : <AddIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  height: 40,
                  px: 3,
                  bgcolor: '#4A148C',
                  '&:hover': { bgcolor: '#380b6b' },
                }}
              >
                {editingId ? 'Actualizar' : 'Agregar'}
              </Button>
              {editingId && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={loading}
                  sx={{ textTransform: 'none', borderRadius: 2, height: 40, px: 3 }}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!review_links_list || review_links_list.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              No hay links configurados aún.
            </Typography>
          ) : (
            review_links_list.map((link: any) => (
              <Box
                key={link.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  bgcolor: editingId === link.id ? '#f3e5f5' : '#fcfcfc',
                  transition: 'background-color 0.3s',
                }}
              >
                <Avatar
                  src={`${window.location.origin}/social/${link.icon}`}
                  alt={link.platform}
                  sx={{ width: 32, height: 32, mr: 2 }}
                  variant="square"
                />
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                    {link.name ? `${link.name} (${link.platform})` : link.platform}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {link.url}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Verificar Link">
                    <IconButton size="small" color="primary" onClick={() => handleVerify(link.url)}>
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton size="small" color="info" onClick={() => handleEdit(link)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(link.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </CardContent>
      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />
    </Card>
  );
};
